import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Film, ImageIcon, X } from 'lucide-react';
import {
  chainForVideo,
  imageVersions,
  videoVersions,
  type Board,
  type BoardImageNode,
  type BoardVideoNode,
} from '../../lib/canvasBoard';
import { getImageModelById } from '../../lib/imageModelCatalog';
import { getVideoModelById } from '../../lib/videoModelCatalog';

type Props = {
  board: Board;
  node: BoardImageNode | BoardVideoNode;
  sceneIndex: Map<string, number>;
  readOnly?: boolean;
  onClose: () => void;
  onSelectImageVersion?: (imageId: string, versionId: string) => void;
  onSelectVideoVersion?: (videoId: string, versionId: string) => void;
};

type Entry = {
  id: string;
  url: string;
  title: string;
  active: boolean;
  createdAt: number;
  details: string[];
  textLabel: string;
  text?: string;
  scenes?: Array<{ label: string; description: string }>;
  /** Absent on entries that are not a stored version (nothing to switch to). */
  versionId?: string;
};

const when = (t: number) => (t
  ? new Date(t).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');

function imageEntries(node: BoardImageNode): Entry[] {
  const p = node.phase;
  const prompt = p.still_prompt_en || (p.description_pt && p.description_pt !== p.label ? p.description_pt : '');
  const versions = imageVersions(node);
  if (!versions.length) {
    return p.imageUrl
      ? [{
          id: 'current',
          url: p.imageUrl,
          title: 'Imagem',
          active: true,
          createdAt: 0,
          details: node.width && node.height ? [`${node.width}×${node.height}`] : [],
          textLabel: 'Prompt',
          text: prompt || undefined,
        }]
      : [];
  }
  return versions.map((v, i) => ({
    id: v.id,
    versionId: v.id,
    url: v.imageUrl,
    title: i === 0 ? 'Original' : `Versão ${i + 1}`,
    active: v.imageUrl === p.imageUrl,
    createdAt: v.createdAt,
    details: [
      v.imageModelId ? getImageModelById(v.imageModelId)?.label || v.imageModelId : '',
      v.width && v.height ? `${v.width}×${v.height}` : '',
      v.credits ? `${v.credits} cr` : '',
    ].filter(Boolean),
    textLabel: i === 0 ? 'Prompt' : 'Pedido',
    text: i === 0 ? prompt || undefined : v.instruction,
  }));
}

const chainScenes = (board: Board, node: BoardVideoNode) =>
  chainForVideo(board, node.id).map((n) => ({ label: n.phase.label, description: n.phase.description_pt }));

function videoEntries(board: Board, node: BoardVideoNode): Entry[] {
  const chain = chainScenes(board, node);
  const versions = videoVersions(node);
  if (!versions.length) {
    return node.videoUrl
      ? [{
          id: 'current',
          url: node.videoUrl,
          title: node.edit ? 'Vídeo editado' : node.fromGallery ? 'Vídeo da galeria' : 'Vídeo',
          active: true,
          createdAt: 0,
          details: [],
          textLabel: 'Roteiro',
          text: node.brief,
          scenes: chain.length ? chain : undefined,
        }]
      : [];
  }
  return versions.map((v, i) => {
    const active = v.clipUrl === node.clipUrl;
    const model = v.videoModelId ? getVideoModelById(v.videoModelId)?.label || v.videoModelId : '';
    return {
      id: v.id,
      versionId: v.id,
      // The active clip plays as the node does (with any upstream joins).
      url: active && node.videoUrl ? node.videoUrl : v.clipUrl,
      title: `Versão ${i + 1}`,
      active,
      createdAt: v.createdAt,
      details: [
        model ? `${model}${v.resolutionId ? ` · ${v.resolutionId}` : ''}` : '',
        v.seconds ? `${v.seconds}s` : '',
        v.credits ? `${v.credits} cr` : '',
      ].filter(Boolean),
      textLabel: 'Roteiro',
      text: v.brief ?? (active ? node.brief : undefined),
      scenes: v.scenes ?? (active && chain.length ? chain : undefined),
    };
  });
}

/** Full-screen view of an image/video node with its generation history on the right. */
export default function CanvasMediaViewer({ board, node, sceneIndex, readOnly, onClose, onSelectImageVersion, onSelectVideoVersion }: Props) {
  const isImage = node.kind === 'image';
  const entries = useMemo(() => (node.kind === 'image' ? imageEntries(node) : videoEntries(board, node)), [board, node]);
  const activeId = entries.find((e) => e.active)?.id ?? entries[entries.length - 1]?.id;
  const [viewId, setViewId] = useState(activeId);
  const shown = entries.find((e) => e.id === viewId) ?? entries.find((e) => e.id === activeId);
  const shownIdx = shown ? entries.indexOf(shown) : -1;
  const videoRef = useRef<HTMLVideoElement>(null);
  const busy = node.kind === 'image' ? node.phase.status === 'generating' : node.status === 'generating' || node.status === 'joining';
  const select = isImage ? onSelectImageVersion : onSelectVideoVersion;
  const scene = sceneIndex.get(node.id);
  /** What will be generated, for nodes with nothing rendered yet. */
  const plan = useMemo(() => (node.kind === 'image'
    ? { text: node.phase.still_prompt_en || node.phase.description_pt, scenes: [] }
    : { text: node.brief, scenes: chainScenes(board, node) }
  ), [board, node]);
  const heading = node.kind === 'image'
    ? `${scene != null ? `Cena ${scene} · ` : ''}${node.phase.label || 'Imagem'}`
    : node.title || 'Vídeo';

  const step = (delta: number) => {
    const next = entries[shownIdx + delta];
    if (next) setViewId(next.id);
  };
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The board's shortcuts (Delete, Shift+letter…) must not act on nodes behind the viewer.
      e.stopPropagation();
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') stepRef.current(-1);
      else if (e.key === 'ArrowRight') stepRef.current(1);
      else if (e.key === ' ' && videoRef.current) {
        e.preventDefault();
        if (videoRef.current.paused) void videoRef.current.play();
        else videoRef.current.pause();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="tb-viewer" role="dialog" aria-modal="true" aria-label={heading}>
      <div className="tb-viewer-stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {shown ? (
          isImage
            ? <img key={shown.id} src={shown.url} alt={heading} />
            : <video key={shown.id} ref={videoRef} src={shown.url} controls controlsList="nodownload" autoPlay playsInline />
        ) : (
          <div className="tb-viewer-empty">{isImage ? 'Imagem ainda não gerada.' : 'Vídeo ainda não gerado.'}</div>
        )}
        {entries.length > 1 && (
          <>
            <button type="button" className="tb-viewer-nav is-prev" aria-label="Versão anterior" disabled={shownIdx <= 0} onClick={() => step(-1)}>
              <ChevronLeft size={22} />
            </button>
            <button type="button" className="tb-viewer-nav is-next" aria-label="Próxima versão" disabled={shownIdx >= entries.length - 1} onClick={() => step(1)}>
              <ChevronRight size={22} />
            </button>
          </>
        )}
        {shown && (
          <div className="tb-viewer-caption">
            <span>{shown.title}{shown.active ? ' · em uso' : ''}</span>
            {!shown.active && shown.versionId && select && !readOnly && (
              <button type="button" disabled={busy} onClick={() => select(node.id, shown.versionId as string)}>
                <Check size={13} /> Usar esta versão
              </button>
            )}
          </div>
        )}
      </div>

      <aside className="tb-viewer-side">
        <header>
          <span className="tb-viewer-kind">{isImage ? <ImageIcon size={14} /> : <Film size={14} />}</span>
          <h3 title={heading}>{heading}</h3>
          <button type="button" className="tb-viewer-close" aria-label="Fechar (Esc)" title="Fechar (Esc)" onClick={onClose}>
            <X size={16} />
          </button>
        </header>
        <div className="tb-viewer-label">Histórico{entries.length > 1 ? ` · ${entries.length} versões` : ''}</div>
        {entries.length > 1 && select && !readOnly && (
          <p className="tb-viewer-hint">Clique para ver. Usar troca a versão deste nó.</p>
        )}
        <ol className="tb-viewer-list">
          {[...entries].reverse().map((e) => (
            <li key={e.id}>
              <div className={`tb-viewer-item${e.id === shown?.id ? ' is-shown' : ''}`}>
                <button
                  type="button"
                  className="tb-viewer-open"
                  onClick={() => setViewId(e.id)}
                >
                  <span className="tb-viewer-thumb">
                    {isImage ? <img src={e.url} alt="" /> : <video src={e.url} muted preload="metadata" />}
                  </span>
                  <span className="tb-viewer-item-body">
                    <span className="tb-viewer-item-title">
                      {e.title}
                      {e.active && <em>em uso</em>}
                      {when(e.createdAt) && <small>{when(e.createdAt)}</small>}
                    </span>
                    {e.details.length > 0 && <span className="tb-viewer-item-meta">{e.details.join(' · ')}</span>}
                    {e.text && (
                      <span className="tb-viewer-item-text">
                        <b>{e.textLabel}</b> {e.text}
                      </span>
                    )}
                    {e.scenes && e.scenes.length > 0 && (
                      <span className="tb-viewer-scenes">
                        {e.scenes.map((s, i) => (
                          <span key={`${i}-${s.label}`}>
                            <b>{i + 1}. {s.label}</b>
                            {s.description && s.description !== s.label ? ` — ${s.description}` : ''}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </button>
                {e.versionId && select && !readOnly && !e.active && (
                  <button
                    type="button"
                    className="tb-viewer-use"
                    disabled={busy}
                    title="Esta passa a ser a versão do nó"
                    onClick={() => {
                      setViewId(e.id);
                      select(node.id, e.versionId as string);
                    }}
                  >
                    Usar
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
        {!entries.length && (
          <div className="tb-viewer-none">
            <p>Ainda sem histórico.</p>
            {plan.text && <p className="tb-viewer-item-text"><b>{isImage ? 'Prompt' : 'Roteiro'}</b> {plan.text}</p>}
            {plan.scenes.length > 0 && (
              <span className="tb-viewer-scenes">
                {plan.scenes.map((s, i) => (
                  <span key={`${i}-${s.label}`}>
                    <b>{i + 1}. {s.label}</b>
                    {s.description && s.description !== s.label ? ` — ${s.description}` : ''}
                  </span>
                ))}
              </span>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
